import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";

const STORAGE_KEY = "dhannniti_chat_session";

const WELCOME = {
    role: "assistant",
    content:
        "Hi, I'm the DhanNniti Concierge. I can answer questions about our packages, EMIs, credit cards, or help you pick where to start. What's on your mind?",
};

const QUICK_PROMPTS = [
    "How do I know which package fits me?",
    "What's the minimum-due trap?",
    "How does debt settlement via your legal team work?",
    "Is my data confidential?",
];

export default function ChatWidget({ onBookClick }) {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([WELCOME]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollerRef = useRef(null);

    // Load prior session
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed?.messages?.length) setMessages(parsed.messages);
                if (parsed?.sessionId) setSessionId(parsed.sessionId);
            }
        } catch {
            // ignore
        }
    }, []);

    // Persist on change
    useEffect(() => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ sessionId, messages }),
            );
        } catch {
            // ignore
        }
    }, [sessionId, messages]);

    // Autoscroll
    useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const send = async (text) => {
        const clean = text.trim();
        if (!clean || sending) return;
        const history = messages.slice(-12); // last 12 turns
        const newMessages = [...messages, { role: "user", content: clean }];
        setMessages(newMessages);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post("/chat", {
                session_id: sessionId,
                message: clean,
                history,
            });
            if (data?.session_id && !sessionId) setSessionId(data.session_id);
            setMessages((m) => [
                ...m,
                { role: "assistant", content: data.reply || "" },
            ]);
        } catch (err) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content:
                        "Sorry, I couldn't reach the server. Please try again in a moment.",
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    const handleLinkClick = (e) => {
        const target = e.target;
        if (target.tagName === "A" && target.getAttribute("href") === "#book") {
            e.preventDefault();
            setOpen(false);
            const el = document.querySelector("#packages");
            if (el) {
                el.scrollIntoView({ behavior: "smooth" });
            } else {
                window.location.href = "/#packages";
            }
        }
    };

    const resetChat = () => {
        setMessages([WELCOME]);
        setSessionId(null);
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // ignore
        }
    };

    return (
        <>
            {/* Floating button */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="chat-open-btn"
                    className="fixed bottom-24 right-4 md:bottom-8 md:right-8 z-40 bg-[#1A5C53] hover:bg-[#13423B] text-white rounded-full shadow-[0_12px_32px_-8px_rgba(13,27,26,0.4)] px-5 py-3.5 flex items-center gap-2 transition-transform hover:scale-105"
                    aria-label="Open chat"
                >
                    <MessageCircle size={18} />
                    <span className="text-sm font-medium hidden sm:inline">
                        Ask DhanNniti
                    </span>
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    data-testid="chat-panel"
                    className="fixed inset-x-0 bottom-0 md:inset-auto md:bottom-8 md:right-8 z-50 md:w-[400px] md:h-[600px] w-full h-[85vh] bg-[#FDFAF5] md:rounded-2xl rounded-t-2xl border border-[#E6DFD3] shadow-[0_24px_80px_-20px_rgba(13,27,26,0.35)] flex flex-col overflow-hidden"
                >
                    {/* Header */}
                    <div className="bg-[#0D1B1A] text-white px-5 py-4 flex items-center justify-between">
                        <div>
                            <div className="font-serif text-lg leading-tight">
                                Dhan<span className="text-[#D4944C]">Nniti</span>{" "}
                                Concierge
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-[#9FB1AE] mt-0.5 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#2D6A4F]" />
                                Online
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={resetChat}
                                className="text-xs text-[#9FB1AE] hover:text-white px-2"
                                title="Start over"
                            >
                                Reset
                            </button>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="chat-close-btn"
                                className="text-[#9FB1AE] hover:text-white p-1"
                                aria-label="Close chat"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div
                        ref={scrollerRef}
                        onClick={handleLinkClick}
                        className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-[#FDFAF5]"
                    >
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                        m.role === "user"
                                            ? "bg-[#1A5C53] text-white rounded-br-md"
                                            : "bg-white border border-[#E6DFD3] text-[#0D1B1A] rounded-bl-md"
                                    }`}
                                >
                                    {m.role === "assistant" ? (
                                        <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-a:text-[#1A5C53] prose-a:font-medium prose-strong:text-[#0D1B1A]">
                                            <ReactMarkdown>
                                                {m.content}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <span>{m.content}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-[#E6DFD3] rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-[#758684] animate-pulse" />
                                    <span
                                        className="w-2 h-2 rounded-full bg-[#758684] animate-pulse"
                                        style={{ animationDelay: "150ms" }}
                                    />
                                    <span
                                        className="w-2 h-2 rounded-full bg-[#758684] animate-pulse"
                                        style={{ animationDelay: "300ms" }}
                                    />
                                </div>
                            </div>
                        )}

                        {messages.length === 1 && !sending && (
                            <div className="pt-2 space-y-2">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-[#758684] px-1">
                                    Try asking
                                </div>
                                {QUICK_PROMPTS.map((q) => (
                                    <button
                                        key={q}
                                        onClick={() => send(q)}
                                        className="block w-full text-left text-sm bg-white border border-[#E6DFD3] hover:border-[#1A5C53] rounded-xl px-3.5 py-2.5 text-[#4A5A58] hover:text-[#0D1B1A] transition-colors"
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send(input);
                        }}
                        className="border-t border-[#E6DFD3] bg-white px-4 py-3 flex items-center gap-2"
                    >
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your question…"
                            data-testid="chat-input"
                            disabled={sending}
                            className="flex-1 bg-[#FDFAF5] border border-[#E6DFD3] rounded-full px-4 py-2.5 text-sm text-[#0D1B1A] placeholder:text-[#9FB1AE] focus:outline-none focus:border-[#1A5C53] transition-colors disabled:opacity-60"
                        />
                        <button
                            type="submit"
                            disabled={sending || !input.trim()}
                            data-testid="chat-send-btn"
                            className="w-10 h-10 rounded-full bg-[#1A5C53] hover:bg-[#13423B] text-white flex items-center justify-center disabled:opacity-40 transition-colors"
                            aria-label="Send"
                        >
                            {sending ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Send size={15} />
                            )}
                        </button>
                    </form>
                    <div className="text-[10px] text-center text-[#758684] pb-2 bg-white">
                        AI-generated · verify with a strategist for decisions
                    </div>
                </div>
            )}
        </>
    );
}
